import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import { useStore } from "../../context/StoreContext";
import { getFullImageUrl } from "../../utils/imageUrl";
import {
  useGetStoreBrandingQuery,
  useGetOrderingCatalogQuery,
} from "../../store/api";

const MenuPreview = () => {
  const { selectedStoreId } = useStore();
  const [storeSlug, setStoreSlug] = useState("");
  const [previewSlug, setPreviewSlug] = useState("");

  const {
    data: branding,
    isLoading: brandingLoading,
    isError: brandingError,
    error: brandingErrorData,
  } = useGetStoreBrandingQuery(previewSlug, {
    skip: !previewSlug,
  });

  const {
    data: catalog,
    isLoading: catalogLoading,
    isError: catalogError,
    error: catalogErrorData,
  } = useGetOrderingCatalogQuery(previewSlug, {
    skip: !previewSlug,
  });

  const handlePreview = (e) => {
    e.preventDefault();
    if (storeSlug.trim()) {
      setPreviewSlug(storeSlug.trim());
    }
  };

  const isLoading = brandingLoading || catalogLoading;
  const hasError = brandingError || catalogError;
  const errorMessage =
    brandingErrorData?.data?.detail || catalogErrorData?.data?.detail;

  const categories = Array.isArray(catalog?.categories)
    ? catalog.categories
    : [];

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Menu Preview"
          subtitle="Preview your customer-facing menu."
        />
        <Card>
          <Alert tone="warning">
            Please select a store to preview the menu.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Menu Preview"
        subtitle="See how your menu appears to customers."
      />

      <Card>
        <form className="space-y-4" onSubmit={handlePreview}>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Store Slug"
                name="storeSlug"
                placeholder="nova-main"
                value={storeSlug}
                onChange={(e) => setStoreSlug(e.target.value)}
                helper="Enter your store's URL slug to preview the menu"
                required
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Loading..." : "Preview Menu"}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {hasError && (
        <Alert tone="error">
          {errorMessage ||
            "Failed to load menu. Please check the store slug and try again."}
        </Alert>
      )}

      {previewSlug && !isLoading && !hasError && branding && (
        <>
          {/* Branding Section */}
          <Card>
            <h3 className="mb-4 text-lg font-semibold text-slate-900">
              Store Branding
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Store Name
                </label>
                <p className="text-base text-slate-900">
                  {branding.storeName || "-"}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Business Name
                </label>
                <p className="text-base text-slate-900">
                  {branding.businessName || "-"}
                </p>
              </div>
              {branding.logoUrl && (
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Logo
                  </label>
                  <img
                    src={branding.logoUrl}
                    alt="Store Logo"
                    className="h-20 w-auto rounded-lg border border-slate-200"
                  />
                </div>
              )}
              {branding.primaryColor && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-8 w-8 rounded border border-slate-200"
                      style={{ backgroundColor: branding.primaryColor }}
                    />
                    <span className="text-sm text-slate-600">
                      {branding.primaryColor}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Menu Catalog Section */}
          <Card>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                Menu Catalog
              </h3>
              <Badge tone="info">{categories.length} Categories</Badge>
            </div>

            {categories.length === 0 ? (
              <Alert tone="warning">
                No categories found. Add categories and items to populate the
                menu.
              </Alert>
            ) : (
              <div className="space-y-6">
                {categories.map((category) => (
                  <div
                    key={category.categoryId}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="mb-3 flex items-start gap-3">
                      {category.imageUrl && (
                        <img
                          src={getFullImageUrl(category.imageUrl)}
                          alt={category.name}
                          className="h-16 w-16 rounded-lg border border-slate-200 object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-slate-900">
                          {category.name}
                        </h4>
                        {category.description && (
                          <p className="mt-1 text-sm text-slate-600">
                            {category.description}
                          </p>
                        )}
                        <div className="mt-2">
                          <Badge tone="neutral">
                            {category.items?.length || 0} Items
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {category.items && category.items.length > 0 && (
                      <div className="mt-3 space-y-2 border-t border-slate-200 pt-3">
                        {category.items.map((item) => (
                          <div
                            key={item.itemId}
                            className="flex items-start gap-3 rounded-lg bg-white p-3"
                          >
                            {item.imageUrl && (
                              <img
                                src={getFullImageUrl(item.imageUrl)}
                                alt={item.name}
                                className="h-12 w-12 rounded border border-slate-200 object-cover"
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h5 className="text-sm font-medium text-slate-900">
                                    {item.name}
                                  </h5>
                                  {item.description && (
                                    <p className="mt-1 text-xs text-slate-600">
                                      {item.description}
                                    </p>
                                  )}
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {item.isVegan && (
                                      <Badge tone="success" className="text-xs">
                                        Vegan
                                      </Badge>
                                    )}
                                    {item.isVegetarian && (
                                      <Badge tone="success" className="text-xs">
                                        Vegetarian
                                      </Badge>
                                    )}
                                    {item.isSpicy && (
                                      <Badge tone="warning" className="text-xs">
                                        Spicy
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <span className="text-sm font-semibold text-slate-900">
                                  ${(item.basePrice || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {!previewSlug && (
        <Card className="bg-slate-50">
          <div className="flex gap-3">
            <svg
              className="h-5 w-5 flex-shrink-0 text-slate-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-slate-900">
                How to Use Menu Preview
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-slate-600">
                <li>
                  • Enter your store's URL slug (e.g., "nova-main") in the field
                  above
                </li>
                <li>
                  • Click "Preview Menu" to see how your menu appears to
                  customers
                </li>
                <li>
                  • This shows the same data that customers see when ordering
                </li>
                <li>
                  • Use this to verify your menu is configured correctly before
                  going live
                </li>
              </ul>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MenuPreview;
